import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Header from './Header';
import Footer from './Footer';
import BottomNav from './BottomNav';
import LogMatchFab from './LogMatchFab';
import MoreSheet from './MoreSheet';
import AuthContext from '../../context/auth/authContext';
import DeckContext from '../../context/decks/deckContext';
import '../../styles/layout/layout.css';

// MainLayout component that serves as the app's layout wrapper
const MainLayout = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { updateActiveDeckId, getDecks } = useContext(DeckContext);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  // Synchronize active deck from auth context to deck context when user loads - only once
  useEffect(() => {
    const hasLoadedDecks = localStorage.getItem('hasLoadedDecks');
    
    if (user && !hasLoadedDecks) {
      // Load decks when user is authenticated (only once)
      getDecks();
      localStorage.setItem('hasLoadedDecks', 'true');
      
      // Sync active deck ID if user has one
      if (user.activeDeck) {
        console.log('Syncing active deck ID from user profile:', user.activeDeck);
        updateActiveDeckId(user.activeDeck);
      }
    }
    
    // Clear flag when user logs out
    if (!user) {
      localStorage.removeItem('hasLoadedDecks');
    }
  }, [user, getDecks, updateActiveDeckId]);

  // Lock body scroll while the mobile "More" sheet is open
  useEffect(() => {
    if (moreOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [moreOpen]);

  // Close the sheet on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Keep --vh/--vvh CSS vars in sync with the real viewport for mobile browsers.
  // Coalesced into a single rAF so rapid resize/orientation events don't thrash
  // layout on the main thread (INP). The old window.scrollTo(0,1) URL-bar hack
  // was removed — modern CSS uses 100dvh instead.
  useEffect(() => {
    let frame = null;

    const applyViewportHeight = () => {
      frame = null;
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      if (window.visualViewport) {
        document.documentElement.style.setProperty('--vvh', `${window.visualViewport.height * 0.01}px`);
      }
    };

    const scheduleViewportHeight = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(applyViewportHeight);
    };

    // Initial measurement (synchronous so first paint has correct vars).
    applyViewportHeight();

    window.addEventListener('resize', scheduleViewportHeight);
    window.addEventListener('orientationchange', scheduleViewportHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleViewportHeight);
    }

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleViewportHeight);
      window.removeEventListener('orientationchange', scheduleViewportHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', scheduleViewportHeight);
      }
    };
  }, []);

  // Auth screens have no app chrome — skip the mobile bottom nav / FAB there.
  const isAuthScreen =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/reset-password');

  // The log-match screens have their own action bar, so the quick-log FAB is
  // redundant there and would crowd it on mobile.
  const isLogMatchScreen = location.pathname.startsWith('/log-match');

  return (
    <>
      <Header onMenu={() => setMoreOpen(true)} />
      <div className="desktop-navbar">
        <Navbar />
      </div>
      <div className={`main-content ${!isAuthScreen ? 'has-bottom-nav' : ''}`}>
        <div className={`container ${location.pathname === '/login' || location.pathname === '/register' ? 'auth-container' : ''}`}>
          <Outlet />
        </div>
      </div>
      <Footer />
      {!isAuthScreen && (
        <>
          {!isLogMatchScreen && <LogMatchFab />}
          <BottomNav onMore={() => setMoreOpen(true)} />
          <MoreSheet isOpen={moreOpen} onClose={() => setMoreOpen(false)} />
        </>
      )}
    </>
  );
};

export default MainLayout;